import React from 'react';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

interface CardProps {
  title?: string;
  subtitle?: string;
  className?: string;
  bodyClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  hoverable?: boolean;
  animate?: boolean;
  glowing?: boolean;
  gradient?: boolean;
  elevated?: boolean;
  glass?: boolean;
}

export const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  className,
  bodyClassName,
  headerClassName,
  footerClassName,
  children,
  footer,
  hoverable = false,
  animate = true,
  glowing = false,
  gradient = false,
  elevated = true,
  glass = false,
}) => {
  const hasHeader = title || subtitle;
  
  const cardVariants = {
    hover: { 
      y: animate && hoverable ? -5 : 0,
      scale: animate && hoverable ? 1.01 : 1,
      transition: { duration: 0.2 }
    },
    initial: {
      y: 0,
      scale: 1,
    }
  };

  return (
    <motion.div
      className={twMerge(
        'rounded overflow-hidden backdrop-blur-sm',
        glass ? 'bg-dark-100/40' : 'bg-dark-100/90',
        'border border-dark-200/30',
        gradient && 'bg-gradient-card',
        elevated && 'shadow-lg shadow-dark-900/20',
        glowing && 'ring-1 ring-primary/20 shadow-lg shadow-primary/10',
        hoverable && [
          'transition-all duration-300',
          'hover:shadow-xl hover:shadow-dark-900/30',
          'hover:border-dark-200/50',
          glowing && 'hover:shadow-glow-lg hover:ring-primary/30'
        ],
        'transform-gpu',
        className
      )}
      initial="initial"
      whileHover="hover"
      variants={cardVariants}
    >
      {hasHeader && (
        <div className={twMerge(
          'px-6 py-4',
          'border-b border-dark-200/30',
          'bg-gradient-dark',
          headerClassName
        )}>
          {title && (
            <h3 className="text-lg font-semibold text-white">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="mt-1 text-sm text-gray-400">
              {subtitle}
            </p>
          )}
        </div>
      )}
      
      <div className={twMerge(
        'p-6',
        glass && 'backdrop-blur-md',
        bodyClassName
      )}>
        {children}
      </div>
      
      {footer && (
        <div className={twMerge(
          'px-6 py-4',
          'bg-dark-200/50',
          'border-t border-dark-200/30',
          'backdrop-blur-sm',
          footerClassName
        )}>
          {footer}
        </div>
      )}
    </motion.div>
  );
};